import {
  Page,
  Button,
  LegacyCard,
  FormLayout,
  TextField,
  Select,
  Banner,
  Icon,
  BlockStack,
  InlineGrid,
  Text,
  Autocomplete,
} from "@shopify/polaris";
import { ViewIcon, HideIcon, SearchIcon } from "@shopify/polaris-icons";
import {
  useGetAccountToEditQuery,
  useUpdateAccountMutation,
} from "../store/store/slices/AccountRTK";
import { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { AccountFormData } from "../store/types/Accounts";
import { showToast } from "../store/store/slices/AlertSlice";
import { useDispatch } from "react-redux";
import { useFetchTcsCitiesQuery } from "../store/store/slices/CitiesRTK";
import { escapeRegExp } from "../utils/helpers";
import { CITY_OPTIONS_LIST, CITY_TYPE } from "../store/types/BookOrder";

export default function EditAccount() {
  const navigateTo = useNavigate();
  const dispatch = useDispatch();
  const { account_id } = useParams<{ account_id: string }>();
  const [options, setOptions] = useState<CITY_OPTIONS_LIST[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
  const {
    data: cities,
    isLoading: cityFetchingLoading,
    isError: isCityFetchingFailed,
    isFetching: citiesRefetching,
    isSuccess: citySuccess,
  } = useFetchTcsCitiesQuery(undefined);
  const {
    data: account,
    isLoading: gettingAccount,
    isError: gettingAccountFailed,
    error: gettingAccountError,
    isSuccess: accountSuccess,
  } = useGetAccountToEditQuery(account_id!);
  const [updateAccount, { isLoading: updating, isError, isSuccess, error }] =
    useUpdateAccountMutation();
  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<AccountFormData>({
    defaultValues: {
      api_password: "",
      name: "",
      is_default: "false",
      in_active: "ACTIVE",
      address_code: "",
      weight: "",
    },
  });
  const onShowPasswordHandler = async () => {
    setShowPassword((prev) => !prev);
  };

  useEffect(() => {
    if (account) {
      reset({
        api_password:
          account.account.company === "POSTEX"
            ? "Postex does not require a password"
            : account.account.api_password || "",
        name: account.account.name || "",
        is_default: account.account.is_default ? "default" : "false",
        address_code: account.account.address_code || "",
        weight: account.account.weight || "",
        shipper_name: account.account.shipper_name || "",
        shipper_mobile: account.account.shipper_mobile || "",
        shipper_address: account.account.shipper_address || "",
        shipper_city: account.account.shipper_city || "",
        client_id: account.account.client_id || "",
        client_secret: account.account.client_secret || "",
        username: account.account.username || "",
        in_active: account.account.in_active ? "IN_ACTIVE" : "ACTIVE",
        pickup_address_id: account.account.pickup_address_id || "",
      });
    }
    return () => {
      reset({
        api_password: "",
        name: "",
        is_default: "false",
        address_code: "",
      });
    };
  }, [account, reset]);

  const onSubmit = async (formData: AccountFormData) => {
    await updateAccount({
      accountId: account_id!,
      accountData: {
        ...formData,
        is_default: formData.is_default === "default",
      },
    });
  };
  useEffect(() => {
    if (isSuccess) {
      dispatch(
        showToast({ error: false, message: "Account updated", visible: true })
      );
      navigateTo("/dashboard/accounts");
    }
  }, [isError, isSuccess, navigateTo, error]);
  const updateText = useCallback(
    (value: string) => {
      setInputValue(value);
      if (value === "") {
        setOptions(cities);
        return;
      }
      const filterRegex = new RegExp(escapeRegExp(value), "i");
      const resultOptions = cities.filter((option: CITY_TYPE) =>
        option.label.match(filterRegex)
      );
      setOptions(resultOptions);
    },
    [cities]
  );

  const updateSelection = useCallback(
    (selected: string[]) => {
      const selectedId = selected[0];
      const matchedOption = cities?.find((option: CITY_TYPE) => {
        if (typeof option?.value === "number")
          return option?.value === Number(selectedId);
        return option?.value === selectedId;
      });
      if (matchedOption) {
        setSelectedOptions([matchedOption.value]);
        setInputValue(matchedOption.label);
      } else {
        setSelectedOptions([]);
        setInputValue("");
      }
    },
    [cities]
  );
  useEffect(() => {
    if (accountSuccess && citySuccess) {
      updateText("KAMALIA");
    }
  }, [account, cities]);

  const textField = (
    <Autocomplete.TextField
      onChange={updateText}
      name="shipper_city"
      label="City"
      value={inputValue}
      prefix={<Icon source={SearchIcon} tone="base" />}
      placeholder="Search"
      autoComplete="off"
      disabled={cityFetchingLoading || citiesRefetching || isCityFetchingFailed}
      loading={cityFetchingLoading || citiesRefetching}
      requiredIndicator
      error={
        isCityFetchingFailed
          ? "City fetching failed"
          : inputValue?.trim() === ""
            ? "Please add destination city"
            : ""
      }
    />
  );

  if (gettingAccountFailed) {
    return (
      <Page
        backAction={{
          content: "Settings",
          url: "",
          onAction() {
            navigateTo(-1);
          },
        }}
        title="Edit Account"
        narrowWidth
      >
        <Banner title="An error occurred" tone="critical">
          <p>{gettingAccountError?.data?.errors[0]?.msg}</p>
        </Banner>
      </Page>
    );
  }
  return (
    <Page
      backAction={{
        content: "Settings",
        url: "",
        onAction() {
          navigateTo(-1);
        },
      }}
      title="Edit Account"
      primaryAction={
        <Button
          variant={theme === "dark" ? "secondary" : "primary"}
          onClick={handleSubmit(onSubmit)}
          loading={updating || gettingAccount}
          disabled={updating}
        >
          Save Changes
        </Button>
      }
      narrowWidth
    >
      <BlockStack gap={"300"}>
        <Banner tone="info">
          <p className="my_5">
            You need to enter account's password everytime you change your
            account credentials.
          </p>
        </Banner>
        <div>
          <Banner tone="info">
            If you want to change api key, let us know at{" "}
            <a href="mailto:support@book-ease.com" target="_blank">
              support@book-ease.com
            </a>
          </Banner>
        </div>
        {account?.account?.company === "TCS" && (
          <LegacyCard sectioned>
            <InlineGrid>
              <FormLayout>
                <Controller
                  control={control}
                  name="client_id"
                  rules={{ required: true, maxLength: 100 }}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      label="Client id"
                      autoComplete="Off"
                      value={value || ""}
                      onChange={onChange}
                      placeholder="Enter client id"
                      disabled
                      requiredIndicator
                      error={
                        errors.shipper_name
                          ? "Client id is required and max length 100"
                          : ""
                      }
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="client_secret"
                  rules={{ required: true, maxLength: 100 }}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      label="Client secret"
                      autoComplete="Off"
                      value={value || ""}
                      onChange={onChange}
                      placeholder="Enter secret"
                      requiredIndicator
                      error={
                        errors.client_secret
                          ? "Client secret is required and max length 100"
                          : ""
                      }
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="username"
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      label="User name"
                      autoComplete="Off"
                      value={value || ""}
                      onChange={onChange}
                      placeholder="Enter user name"
                      disabled
                      requiredIndicator
                    />
                  )}
                />
              </FormLayout>
            </InlineGrid>
          </LegacyCard>
        )}
        <LegacyCard sectioned>
          <FormLayout>
            {account?.account?.company !== "POSTEX" && account?.account?.company !== "TRAX" && (
              <Controller
                control={control}
                name="api_password"
                rules={{
                  required: account?.account?.company !== "POSTEX",
                  maxLength: 100,
                }}
                render={({ field }) => (
                  <div style={{ position: "relative" }}>
                    <span
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "20px",
                        padding: "0",
                        position: "absolute",
                        right: "5px",
                        top: "30px",
                        zIndex: "100000",
                        cursor: "pointer",
                      }}
                      onClick={onShowPasswordHandler}
                    >
                      <Icon source={!showPassword ? ViewIcon : HideIcon} />
                    </span>
                    <TextField
                      label="Api Password"
                      {...field}
                      type={showPassword ? "text" : "password"}
                      loading={gettingAccount}
                      autoComplete="api-password"
                      placeholder="Api password"
                      disabled={account?.account?.company === "POSTEX"}
                      error={
                        errors.api_password
                          ? "API password is required (max 100)"
                          : ""
                      }
                    />
                  </div>
                )}
              />
            )}
            {account?.account?.company === "POSTEX" && (
              <Controller
                control={control}
                name="address_code"
                rules={{ required: true, maxLength: 50 }}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    label="Address code"
                    value={value || ""}
                    autoComplete="Off"
                    onChange={onChange}
                    placeholder="Enter postex address code"
                    requiredIndicator
                    error={
                      errors.address_code
                        ? "Address code is required (max 50)"
                        : ""
                    }
                  />
                )}
              />
            )}
            {account?.account?.company === "TRAX" && (
              <Controller
                control={control}
                name="pickup_address_id"
                rules={{ required: true, maxLength: 500 }}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    label="Pickup Address ID"
                    value={value || ""}
                    autoComplete="Off"
                    onChange={onChange}
                    placeholder="Enter pickup address id provided by TRAX"
                    loading={gettingAccount}
                    requiredIndicator
                    error={
                      errors.pickup_address_id
                        ? "Pickup address ID is required (max 500 characters)"
                        : ""
                    }
                  />
                )}
              />
            )}
            <Controller
              control={control}
              name="name"
              rules={{ required: "Account name is required", maxLength: 30 }}
              render={({ field }) => (
                <TextField
                  label="Account Name"
                  {...field}
                  loading={gettingAccount}
                  autoComplete="account-name"
                  placeholder="'My Account'"
                  requiredIndicator
                  error={errors.name ? "Name is required (max 30)" : ""}
                />
              )}
            />
            <Controller
              control={control}
              name="weight"
              rules={{ required: false }}
              render={({ field }) => (
                <TextField
                  label="Weight: (in grams)"
                  {...field}
                  type="number"
                  loading={gettingAccount}
                  min={0}
                  autoComplete="weight"
                  placeholder="Enter weight"
                />
              )}
            />
            <Controller
              control={control}
              name="is_default"
              render={({ field }) => (
                <Select
                  {...field}
                  label={
                    <p>
                      Set As Default:{" "}
                      <span style={{ fontSize: "12px" }}>
                        You can set an account default only when you haven't
                        already
                      </span>
                    </p>
                  }
                  options={[
                    { label: "No", value: "false" },
                    { label: "Default", value: "default" },
                  ]}
                  disabled={updating || gettingAccount}
                />
              )}
            />
            <Controller
              control={control}
              name="in_active"
              render={({ field }) => (
                <Select
                  {...field}
                  label={
                    <p>
                      Set Activity:{" "}
                      <span style={{ fontSize: "12px" }}>
                        Inactive accounts will not be displayed on booking page
                      </span>
                    </p>
                  }
                  options={[
                    { label: "ACTIVE", value: "ACTIVE" },
                    { label: "IN ACTIVE", value: "IN_ACTIVE" },
                  ]}
                  disabled={updating || gettingAccount}
                />
              )}
            />
          </FormLayout>
        </LegacyCard>
        <LegacyCard>
          {account?.account?.company === "TCS" && (
            <>
              <LegacyCard
                sectioned
                title={
                  <Text as="span" fontWeight="bold">
                    Shipper info{" "}
                    <span style={{ fontWeight: "400" }}>Required for TCS</span>
                  </Text>
                }
              >
                <InlineGrid columns={{ xs: 1, lg: 1 }} gap={"400"}>
                  <Controller
                    control={control}
                    name="shipper_name"
                    rules={{ required: true, maxLength: 100 }}
                    render={({ field: { onChange, value } }) => (
                      <TextField
                        label="Shipper name"
                        autoComplete="Off"
                        value={value || ""}
                        onChange={onChange}
                        placeholder="Enter shipper name"
                        requiredIndicator
                        error={
                          errors.shipper_name
                            ? "Shipper name is required and max length 100"
                            : ""
                        }
                      />
                    )}
                  />

                  <Autocomplete
                    options={options || []}
                    selected={selectedOptions}
                    onSelect={updateSelection}
                    textField={textField}
                  />
                  <Controller
                    control={control}
                    name="shipper_address"
                    rules={{ required: true, maxLength: 100 }}
                    render={({ field: { onChange, value } }) => (
                      <TextField
                        label="Shipper address"
                        autoComplete="Off"
                        value={value || ""}
                        onChange={onChange}
                        placeholder="Enter shipper address"
                        requiredIndicator
                        error={
                          errors.shipper_address
                            ? "Shipper address is required for TCS and max length 100"
                            : ""
                        }
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="shipper_mobile"
                    rules={{ required: true, maxLength: 100 }}
                    render={({ field: { onChange, value } }) => (
                      <TextField
                        label="Shipper mobile"
                        autoComplete="Off"
                        value={value || ""}
                        onChange={onChange}
                        placeholder="Enter shipper mobile"
                        requiredIndicator
                        error={
                          errors.shipper_mobile
                            ? "Shipper mobile number is required for TCS and max length 100"
                            : ""
                        }
                      />
                    )}
                  />
                </InlineGrid>
              </LegacyCard>
            </>
          )}
        </LegacyCard>
      </BlockStack>
    </Page>
  );
}
